import {
  channelRoute,
  channelTabNotes,
  channelTabsFrom,
  channelTabSupportsSort,
  CHANNEL_VIDEO_SORTS,
  isSearchPagingExhausted,
  jumpOutLinks,
  normalizeChannelSort,
  feedScrape,
  formatRoute,
  pickImage,
  retargetVideoFrameUrl,
  richTextToPlain,
  type ChannelDetails,
  type ChannelListing,
  type ChannelListTab,
  type ChannelTab,
  type ChannelTabNote,
  type ChannelVideoSort,
  type FeedItem,
  type InstanceConfig,
  type ParsedList,
  type PlaylistSummary,
  type Result,
  type VideoSummary,
} from '@visious/core';
import { describeProblem, type ProblemCopy } from '../../api/problem';
import type { ApiProblem } from '../../api/videos';
import { fmt, text } from '../../strings';
import {
  toListingItems,
  toListingRows,
  type ListingItem,
  type ListingRow,
  type ProgressLookup,
} from '../../listing/model';
import { toRichNodeViews, type RichNodeView } from '../../richText/model';
import { actionMenu, type MenuModel } from '../../ui/menuModel';

/**
 * §8's Channel page, as reasoning — R13: no DOM, no React, no `RefObject`.
 * `useChannel.ts` feeds it and `view.tsx` renders what it returns.
 *
 * The shape follows 2c's: **the tab and the sort are in the URL**, so this screen holds no state of
 * its own beyond the pages that have been loaded. A tab is a `<Link>`, the sort menu is four
 * `<Link>`s, and the back button is what "go back to Videos" means.
 */

/**
 * Re-exported so `view.tsx` can name them in its props without importing core — R13's rule, enforced by
 * dependency-cruiser, and the same shape `screens/search/model.ts` uses for `SearchQuery`.
 */
export type { ChannelTab, ChannelVideoSort };

/* ------------------------------------------------------------------------------------------------
 * The header
 * ---------------------------------------------------------------------------------------------- */

/**
 * The narrowest avatar that covers a 96 px box at 2×. `authorThumbnails[]` is sorted **ascending**,
 * which is the asymmetry `pickImage` exists to hide, and 176 is the size a listing row already asks
 * for — so the header reuses a cached image rather than fetching a fifth one.
 */
const AVATAR_MIN_WIDTH = 176;

/**
 * 320 for a card, for 2b's reason: only three of the nine declared `videoThumbnails` sizes are 16:9,
 * so the choice is 320×180 at 9–15 KB or 1280×720 at 102 KB and nothing between.
 */
const CARD_THUMBNAIL_MIN_WIDTH = 320;

export interface ChannelHeader {
  readonly channelId: string;
  readonly name: string;
  readonly verified: boolean;
  readonly autoGenerated: boolean;
  readonly avatarUrl: string | null;
  /**
   * `null` when the record carried none. Never sized from the declared dimensions: the API declares
   * four sizes for one URL and calls the last of them 512×288, for an image that is 2176×360.
   */
  readonly bannerUrl: string | null;
  readonly subscriberText: string | null;
  readonly subscriberTitle: string | null;
  /**
   * What the header can say beside the subscriber count — and it is deliberately short. `totalViews`
   * and `joined` are `0` on every channel measured, so there is nothing to put here but a note that
   * they are missing, which belongs on the About tab rather than in a byline.
   */
  readonly badges: readonly string[];
}

export function toChannelHeader(details: ChannelDetails): ChannelHeader {
  const subscribers = details.subscriberCount;
  const badges: string[] = [];
  if (details.autoGenerated) badges.push(text().channel.badge.autoGenerated);
  if (details.ageGated) badges.push(text().channel.badge.ageGated);

  return {
    channelId: details.channelId,
    name: details.name,
    verified: details.verified,
    autoGenerated: details.autoGenerated,
    avatarUrl: pickImage(details.avatars, { minWidth: AVATAR_MIN_WIDTH })?.url ?? null,
    // The first entry, not `pickImage`: all four are the same URL, so "choosing" between them is
    // theatre and the widest declared one is as arbitrary as the narrowest.
    bannerUrl: details.banners[0]?.url ?? null,
    subscriberText: subscribers === null ? null : fmt().count(subscribers),
    subscriberTitle:
      subscribers === null
        ? null
        : fmt().distinctExactTitle(fmt().count(subscribers), fmt().exactCount(subscribers)),
    badges,
  };
}

/* ------------------------------------------------------------------------------------------------
 * The tab strip and the sort menu
 * ---------------------------------------------------------------------------------------------- */

export interface ChannelTabLink {
  readonly tab: ChannelTab;
  readonly label: string;
  readonly href: string;
  readonly current: boolean;
}

/** A function, not a constant: a module-level table freezes whichever language booted first. */
function tabLabels(): Readonly<Record<ChannelTab, string>> {
  return text().channel.tab;
}

/**
 * The strip, from the channel's own `tabs` array. A tab the API omits is dropped rather than shown
 * empty — measured, its endpoint answers `{"videos":[]}` forever.
 *
 * **Except the one being looked at**, which is 4a's fix and why `current` reaches core now: a route may
 * name a tab the record omits, and dropping it left four links with none of them marked current. See
 * `channelTabsFrom`; the matching sentence for the body is `EMPTY_UNDECLARED`.
 *
 * The sort carries across a tab change even where it cannot be honoured, which is
 * `channelTabWireParams`' job to filter. Losing it on the way to Playlists and back would mean the
 * reader's "oldest first" silently became "newest" because they looked at a playlist.
 */
export function toTabLinks(
  details: ChannelDetails,
  current: ChannelTab,
  sort: ChannelVideoSort,
): readonly ChannelTabLink[] {
  return channelTabsFrom(details.tabs, current).map((tab) => ({
    tab,
    label: tabLabels()[tab],
    href: formatRoute(channelRoute(details.channelId, { tab, sort })),
    current: tab === current,
  }));
}

function sortLabels(): Readonly<Record<ChannelVideoSort, string>> {
  const words = text().channel.sort;
  return { newest: words.newest, oldest: words.oldest, popular: words.popular };
}

/**
 * The sort control, as a `MenuModel` — the shape 2c lifted into `ui/menuModel.ts`.
 *
 * `null` for a tab with no working sort, which is Playlists and About. That is not tidiness: on
 * `/playlists` an unrecognised `sort_by` answers `200 []`, so a control offering "Most viewed" there
 * would empty a channel's playlists and read as the channel having none.
 *
 * The non-default options used to carry self-verification details ("From the channel's first
 * upload") — they were removed; the measurements live on in `docs/api/channels.md`.
 */
export function toSortMenu(tab: ChannelTab, current: ChannelVideoSort): MenuModel | null {
  if (!channelTabSupportsSort(tab)) return null;
  const words = text().channel.sort;
  const labels = sortLabels();
  return {
    label: words.label,
    value: labels[current],
    note: null,
    sections: [
      {
        title: null,
        items: CHANNEL_VIDEO_SORTS.map((sort) => ({
          id: sort,
          label: labels[sort],
          detail: null,
          selected: sort === current,
        })),
      },
    ],
  };
}

/** The href for a sort item, so `view.tsx` builds no URLs (R13). */
export function sortHref(channelId: string, tab: ChannelTab, itemId: string): string {
  return formatRoute(channelRoute(channelId, { tab, sort: normalizeChannelSort(itemId) }));
}

/* ------------------------------------------------------------------------------------------------
 * The About tab
 * ---------------------------------------------------------------------------------------------- */

/**
 * What the About tab can actually show, which is less than §8 implies and the difference is measured.
 *
 * There is **no `links` field** in the response, and `totalViews` and `joined` are `0` on 10 of 10
 * channels. So the tab is: the description (as R8 nodes, since that is where a creator's links
 * survive), the subscriber count, the channel keywords, and a sentence saying the two figures a
 * reader expects are not served here. Inventing "0 views · joined 1 Jan 1970" from the zeros is the
 * failure this exists to avoid.
 */
export interface ChannelAbout {
  /**
   * Already resolved to hrefs (R13). A channel description carries hashtags and `@mentions` like any
   * other, and a `timestamp` node cannot appear at all — `parseDescriptionNodes` gates those on a
   * duration and a channel record has none.
   */
  readonly descriptionNodes: readonly RichNodeView[];
  readonly hasDescription: boolean;
  /**
   * The way out to the same channel elsewhere — where the subscriber figure used to sit,
   * which the header above already states. Built from the channel's *route* at its default tab, so
   * the round trip is lossless the way every §8 jump-out is. One "View on" menu since the merge
   * (2026-08-02) — the watch page's shape, in this tab's own verb.
   */
  readonly jumpOut: MenuModel;
  readonly totalViewsText: string | null;
  readonly joinedEpochSec: number | null;
  readonly tags: readonly string[];
}

export function toChannelAbout(details: ChannelDetails, config: InstanceConfig | null): ChannelAbout {
  return {
    descriptionNodes: toRichNodeViews(details.descriptionNodes),
    hasDescription: richTextToPlain(details.descriptionNodes).trim() !== '',
    jumpOut: actionMenu(
      text().channel.page.jumpOut.label,
      jumpOutLinks(config, channelRoute(details.channelId)).map((link) => ({
        id: link.target,
        // The destination's name alone — the host beside it said the same thing twice.
        label: text().channel.page.jumpOut.targets[link.target],
        detail: null,
        selected: false,
        href: link.url,
      })),
    ),
    totalViewsText: details.totalViews === null ? null : fmt().count(details.totalViews),
    joinedEpochSec: details.joinedEpochSec,
    tags: details.tags,
  };
}

/* ------------------------------------------------------------------------------------------------
 * The list tabs
 * ---------------------------------------------------------------------------------------------- */

/**
 * The prose for `ChannelTabNote`, which core returns as ids and counts (R7) — and `null` for the three
 * notes that have no prose any more.
 *
 * Core still computes all four, because "how many rows this tab dropped" is a fact and the tests hold
 * it. What changed is who it is for: a placeholder duration, a row that failed to parse and a list that
 * came back with titles only are all statements about the response, and a reader can act on none of
 * them. Only the live-stream note survives, because that one is about the videos in front of them.
 */
export function noticeTextFor(note: ChannelTabNote): string | null {
  const words = text().channel.notice;
  switch (note.id) {
    case 'streams-live-unreported':
      return note.count === 1
        ? words.streamsLiveUnreportedOne
        : words.streamsLiveUnreported(note.count);
    case 'shorts-placeholders':
    case 'degenerate':
    case 'dropped':
      return null;
  }
}

export interface ChannelNotice {
  readonly id: string;
  readonly text: string;
}

export type ChannelListState =
  | { readonly kind: 'loading'; readonly skeletonCards: number }
  | { readonly kind: 'problem'; readonly copy: ProblemCopy }
  | { readonly kind: 'empty'; readonly title: string; readonly detail: string }
  | {
      readonly kind: 'ready';
      readonly rows: readonly ListingRow[];
      readonly notices: readonly ChannelNotice[];
      /** `null` when the last page carried no continuation — the only end-of-list signal. */
      readonly loadMore: 'available' | 'loading' | null;
      /** Exposed because it is the number the reader is looking at, and the driven checks assert it. */
      readonly loaded: number;
      readonly pages: number;
    };

export interface ChannelListOptions {
  readonly nowEpochSec: number;
  readonly loadingMore: boolean;
  /**
   * Whether the channel record claims this tab (`channelDeclaresTab`). It changes one thing and it is
   * the sentence under an empty list: a tab the record omits is empty *by construction*, not because
   * the creator has published nothing.
   */
  readonly declared: boolean;
  /** 4b's stored playheads (`useWatchProgressLookup`). The playlists tab has no use for one. */
  readonly progress: ProgressLookup;
}

const SKELETON_CARDS = 12;

/**
 * A video tab's rows. `channelRowFacts` is threaded through `toListingItems` by pre-computing the
 * scrape and then correcting per tab — see the note on `absoluteDates` for why the date is suppressed
 * here in two independent ways.
 */
export function videoTabState(
  tab: Exclude<ChannelListTab, 'playlists'>,
  listing: ChannelListing<VideoSummary> | null,
  problem: ApiProblem | null,
  options: ChannelListOptions,
): ChannelListState {
  if (problem !== null) return { kind: 'problem', copy: describeProblem(problem, tabProblem()) };
  if (listing === null) return { kind: 'loading', skeletonCards: SKELETON_CARDS };

  const videos = listing.items;
  if (videos.length === 0) {
    if (listing.droppedCount > 0) {
      return {
        kind: 'problem',
        copy: {
          title: text().channel.allDropped.title,
          detail: text().channel.allDropped.detail,
          retryable: true,
        },
      };
    }
    return emptyTab(tab, options.declared);
  }

  const scrape = feedScrape(videos, options.nowEpochSec);
  const items = toListingItems(videos, {
    nowEpochSec: options.nowEpochSec,
    thumbnailMinWidth: CARD_THUMBNAIL_MIN_WIDTH,
    // Never, on any channel tab. `published` is back-computed from the coarse relative text — every
    // row of one response shares a single second-of-day — so "27 July 2024" would be an invention with
    // up to six months of error in it. Same reading as search's and `recommendedVideos`'.
    absoluteDates: false,
    scrape,
    progress: options.progress,
  });

  // The per-tab correction, applied on top: `toListingItems` cannot know which endpoint answered, and
  // the Shorts tab's duration is a *constant* rather than a zero, so no per-field rule can catch it.
  const corrected = tab === 'shorts' ? items.map(stripBadgeAndDate) : items;

  return {
    kind: 'ready',
    rows: corrected.map((video) => ({ kind: 'video', key: `video:${video.videoId}`, video })),
    notices: channelTabNotes(tab, videos, options.nowEpochSec, scrape, listing.droppedCount).flatMap(
      (note) => {
        const words = noticeTextFor(note);
        return words === null ? [] : [{ id: note.id, text: words }];
      },
    ),
    loadMore: listing.continuation === null ? null : options.loadingMore ? 'loading' : 'available',
    loaded: videos.length,
    pages: listing.pages,
  };
}

/**
 * A `ListingItem` is already formatted, so the Shorts correction happens on the *view model* rather
 * than by re-running `channelRowFacts` — the alternative is a second `toListingItems` variant that
 * takes a tab, and one call site is not a pattern (`ui/index.ts`'s rule). The core function is still
 * the specification: `channelRowFacts('shorts', …)` is what the tests assert, and this is the two
 * fields it nulls, spelled out where they are strings.
 */
function stripBadgeAndDate(item: ListingItem): ListingItem {
  return { ...item, badge: null, publishedText: null, publishedTitle: null };
}

export function playlistTabState(
  listing: ChannelListing<PlaylistSummary> | null,
  problem: ApiProblem | null,
  options: ChannelListOptions,
): ChannelListState {
  if (problem !== null) return { kind: 'problem', copy: describeProblem(problem, tabProblem()) };
  if (listing === null) return { kind: 'loading', skeletonCards: SKELETON_CARDS };

  if (listing.items.length === 0) return emptyTab('playlists', options.declared);

  return {
    kind: 'ready',
    rows: listing.items.map((playlist) => ({
      kind: 'playlist',
      key: `playlist:${playlist.playlistId}`,
      playlist: {
        playlistId: playlist.playlistId,
        href: formatRoute({ kind: 'playlist', playlistId: playlist.playlistId }),
        title: playlist.title,
        author: playlist.author,
        // The byline is the channel whose page this is, so it is not repeated on 30 cards.
        authorHref: null,
        authorVerified: false,
        /*
         * `retargetVideoFrameUrl` for the `/vi/` form — 275 KB → 17 KB, 2c's measurement — and it
         * returns the `/pl_c/` form untouched, which is required rather than incidental: that URL's
         * `sqp` parameter is *load-bearing* (404 without it) and encodes the size, which differs per
         * playlist. Measured on this tab: 24 rows `/vi/` served at 320×180, 6 rows `/pl_c/` served at
         * 426×240, into a box drawn 278×157.
         */
        thumbnailUrl:
          playlist.thumbnail === null
            ? null
            : retargetVideoFrameUrl(playlist.thumbnail.url, 'mqdefault'),
        videoCountText:
          playlist.videoCount === null || playlist.videoCount <= 0
            ? null
            : fmt().count(playlist.videoCount),
      },
    })),
    notices: [],
    loadMore: listing.continuation === null ? null : options.loadingMore ? 'loading' : 'available',
    loaded: listing.items.length,
    pages: listing.pages,
  };
}

/**
 * An empty tab, and **which of the two empties it is**.
 *
 * The list is identical in both cases and the reason is not, so the sentence has to differ. A tab the
 * record declares came back with nothing this time and might not next time; a tab it omits answers
 * `{"videos":[]}` on every request that will ever be made to it — verified in that direction, 2 of 2 —
 * so "this channel has not published any" would be a claim about the creator drawn from a fact about
 * the record. The old `videos` sentence said *"The channel lists a Videos tab and the instance returned
 * no rows for it"*, which was exactly wrong in the case that reaches this arm from a typed URL.
 */
/* ------------------------------------------------------------------------------------------------
 * In-channel search — `/api/v1/channels/{ucid}/search`, §7f
 * ---------------------------------------------------------------------------------------------- */

/** Page 1 of a search, ready for `formatRoute` — so the view builds no URLs (R13). */
export function channelSearchHref(channelId: string, q: string): string {
  return formatRoute({ kind: 'channelSearch', channelId, q, page: CHANNEL_SEARCH_FIRST_PAGE });
}

/** The channel itself — where clearing an applied search goes: `/channel/UCID`, no question. */
export function channelClearSearchHref(channelId: string): string {
  return formatRoute(channelRoute(channelId));
}

const CHANNEL_SEARCH_FIRST_PAGE = 1;

/**
 * The search results, folded from every page fetched so far — the search screen's own fold, over the
 * endpoint that behaves like `/search` in every measured way (§7f): a bare mixed array, `page=`
 * paging, no total, an order that is not even stable between identical requests. So the rules are
 * search's rules — rows dedupe by key across pages, dates stay relative (`published` is back-computed
 * here too), and the end is *proved* by a refused or empty next page, never assumed.
 *
 * It answers in `ChannelListState`, deliberately: the body below the tab strip renders one shape
 * whether it is a tab or a search, and the words are the only difference.
 */
export function channelSearchState(
  q: string,
  startPage: number,
  results: readonly Result<ParsedList<FeedItem>, ApiProblem>[] | null,
  options: {
    readonly nowEpochSec: number;
    readonly loadingMore: boolean;
    readonly progress: ProgressLookup;
  },
): ChannelListState {
  if (results === null || results.length === 0) {
    return { kind: 'loading', skeletonCards: SKELETON_CARDS };
  }

  const rows: ListingRow[] = [];
  const seen = new Set<string>();
  let end = false;

  for (const [index, result] of results.entries()) {
    if (!result.ok) {
      // The one failure that is not a failure: page N+1 of a result set that only had N.
      if (
        result.error.kind === 'transport' &&
        isSearchPagingExhausted(result.error.error, startPage + index)
      ) {
        end = true;
        break;
      }
      return { kind: 'problem', copy: describeProblem(result.error, tabProblem()) };
    }

    const { items } = result.value;
    // `200 []` is the other spelling of "no more" — an empty page is proof, and offering Load more
    // against it would loop forever.
    if (items.length === 0) end = true;

    // Per-page scrape, search's reason: grading one page's scrape over another page's rows would
    // let a healthy page lend its durations to a degenerate one.
    const scrape = feedScrape(
      items.flatMap((item) => (item.kind === 'video' ? [item.video] : [])),
      options.nowEpochSec,
    );
    const { rows: pageRows } = toListingRows(items, {
      nowEpochSec: options.nowEpochSec,
      thumbnailMinWidth: CARD_THUMBNAIL_MIN_WIDTH,
      absoluteDates: false,
      scrape,
      progress: options.progress,
    });
    for (const row of pageRows) {
      if (seen.has(row.key)) continue;
      seen.add(row.key);
      rows.push(row);
    }
  }

  if (rows.length === 0) {
    const words = text().channel.search;
    return { kind: 'empty', title: words.emptyTitle, detail: words.emptyDetail(q) };
  }

  return {
    kind: 'ready',
    rows,
    notices: [],
    loadMore: end ? null : options.loadingMore ? 'loading' : 'available',
    loaded: rows.length,
    pages: results.length,
  };
}

function emptyTab(tab: ChannelListTab, declared: boolean): ChannelListState {
  const words = text().channel.empty;
  if (declared) return { kind: 'empty', title: words[tab].title, detail: words[tab].detail };
  /*
   * The **title** names the tab ("This channel has no Live tab") and the **detail** is a sentence about
   * its contents, so they take different words for the same section — `tabNoun`, because "There
   * are no Live on this channel" is not one.
   */
  return {
    kind: 'empty',
    title: words.undeclaredTitle(tabLabels()[tab]),
    detail: words.undeclaredDetail(text().channel.tabNoun[tab]),
  };
}

/**
 * The two sentences that are genuinely about a channel tab; `api/problem.ts` already writes the other
 * seven once. The third one is new in 2d and it is the reason `ProblemSubject` grew an arm: a channel
 * is the first subject where `upstream-broken` has a *specific*, reachable cause worth naming.
 */
function tabProblem(): {
  readonly notFound: ProblemCopy;
  readonly unreadable: (error: { entity: string; field: string | null; reason: string }) => ProblemCopy;
  readonly upstreamBroken: ProblemCopy;
} {
  const words = text().channel.problem;
  return {
    notFound: words.notFound,
    unreadable: () => words.unreadable,
    upstreamBroken: words.upstreamBroken,
  };
}

export const channelProblemSubject = tabProblem;
